/**
 * Message State Manager Service
 *
 * Handles message state orchestration including:
 * - Merging local and remote messages
 * - Deduplication and sorting
 * - Pagination index management
 * - CSAT feedback filtering
 */

import type { GetMessagesResponseItem } from "@9amhealth/openapi";
import { CHAT_PAGINATION } from "../constants/ChatConstants";
import { METADATA_MESSAGE_TYPE } from "src/constants/misc";
import asTemplateValue from "lib/asTemplateValue";
import type { ChatMessage } from "../ChatBloc";
import { ChatMessageMetaType } from "../ChatBloc";

const KEY_FIRST_ITEM_INDEX = CHAT_PAGINATION.KEY_FIRST_ITEM_INDEX;

export interface MessageCounts {
  local: number;
  remote: number;
}

export interface MergedMessagesResult {
  messages: ChatMessage[];
  firstItemIndex: number;
  toCache: GetMessagesResponseItem[];
}

/**
 * MessageStateManager orchestrates message state transformations:
 * - Filters and merges local and remote messages
 * - Deduplicates messages by ID
 * - Sorts chronologically by timestamp
 * - Manages pagination indices
 * - Filters CSAT feedback messages with empty text
 */
export class MessageStateManager {
  /**
   * Tracks the key message ID used for pagination anchor
   */
  private keyMessageIndexId: string | null = null;

  /**
   * Filter out local messages (messages with computed.isLocal = true)
   */
  withoutLocalMessages(messages: ChatMessage[]): ChatMessage[] {
    return messages.filter((m) => !m.computed.isLocal);
  }

  /**
   * Count local and remote messages in a list
   */
  countMessages(messages: ChatMessage[]): MessageCounts {
    const counts: MessageCounts = { local: 0, remote: 0 };

    for (const message of messages) {
      if (message.computed.isLocal) {
        counts.local++;
      } else {
        counts.remote++;
      }
    }

    return counts;
  }

  /**
   * Merge and deduplicate messages from multiple sources
   * Returns unique messages (by ID) and remote messages for caching
   */
  private mergeAndDeduplicate(
    allInstanceMessages: ChatMessage[],
    stateMessages: ChatMessage[]
  ): { unique: ChatMessage[]; toCache: ChatMessage[] } {
    const combined = [...allInstanceMessages, ...stateMessages];

    // Deduplicate by ID
    const unique = combined.filter(
      (msg, index, self) => self.findIndex((m) => m.id === msg.id) === index
    );

    return { unique, toCache: [...unique] };
  }

  /**
   * Add local messages to the message list
   */
  private addLocalMessages(
    messageList: ChatMessage[],
    localMessages: ChatMessage[]
  ): ChatMessage[] {
    return [...localMessages, ...messageList];
  }

  /**
   * Sort messages chronologically by timestamp
   */
  private sortByTimestamp(messages: ChatMessage[]): ChatMessage[] {
    return messages.sort((a, b) => {
      const aDate = new Date(a.timestamp);
      const bDate = new Date(b.timestamp);
      return aDate.getTime() - bDate.getTime();
    });
  }

  /**
   * Calculate pagination index to keep key message at consistent position
   */
  private calculatePaginationIndex(
    messageList: ChatMessage[],
    currentFirstItemIndex: number
  ): number {
    let firstItemIndex = currentFirstItemIndex;

    // Initialize key message on first call
    if (!this.keyMessageIndexId) {
      this.keyMessageIndexId = messageList[0]?.id ?? null;
    }

    // Always keep the message with keyMessageIndexId at KEY_FIRST_ITEM_INDEX
    if (this.keyMessageIndexId) {
      const findKeyIndex = messageList.findIndex(
        (m) => m.id === this.keyMessageIndexId
      );
      firstItemIndex = KEY_FIRST_ITEM_INDEX - findKeyIndex;
    }

    return firstItemIndex;
  }

  /**
   * Filter out CSAT feedback messages with empty text
   */
  private filterCsatFeedback(messages: ChatMessage[]): ChatMessage[] {
    return messages.filter(
      (message) =>
        !(
          asTemplateValue(
            message.apiItem?.metadata?.[METADATA_MESSAGE_TYPE]
          ) === ChatMessageMetaType.csatFeedback && !message.text?.length
        )
    );
  }

  /**
   * Main orchestration method: merge, deduplicate, sort, and prepare messages for state
   *
   * @param allInstanceMessages - All messages from ChatBloc instance
   * @param currentStateMessages - Current messages in state
   * @param localMessages - Parsed local messages from queue
   * @param currentFirstItemIndex - Current pagination index
   * @returns Merged result with messages, pagination index, and messages to cache
   */
  mergeMessagesForState(
    allInstanceMessages: ChatMessage[],
    currentStateMessages: ChatMessage[],
    localMessages: ChatMessage[],
    currentFirstItemIndex: number
  ): MergedMessagesResult {
    // 1. Filter out local messages from both sources
    const allWithoutLocal = this.withoutLocalMessages(allInstanceMessages);
    const stateWithoutLocal = this.withoutLocalMessages(currentStateMessages);

    // 2. Merge and deduplicate
    const { unique, toCache } = this.mergeAndDeduplicate(
      allWithoutLocal,
      stateWithoutLocal
    );

    // 3. Add local messages
    let messageList = this.addLocalMessages(unique, localMessages);

    // 4. Sort by timestamp
    messageList = this.sortByTimestamp(messageList);

    // 5. Calculate pagination index
    const firstItemIndex = this.calculatePaginationIndex(
      messageList,
      currentFirstItemIndex
    );

    // 6. Filter CSAT feedback
    const filteredMessages = this.filterCsatFeedback(messageList);

    // 7. Extract API items for caching (filter out undefined)
    const apiItemsToCache = toCache
      .map((m) => m.apiItem)
      .filter((item): item is GetMessagesResponseItem => item !== undefined);

    return {
      messages: filteredMessages,
      firstItemIndex,
      toCache: apiItemsToCache
    };
  }

  /**
   * Check if state update is needed
   * Compares current state counts with new counts to avoid unnecessary updates
   */
  shouldUpdateState(
    currentCounts: MessageCounts,
    remoteMsgCount: number,
    localMsgCount: number
  ): boolean {
    return (
      remoteMsgCount !== currentCounts.remote ||
      localMsgCount !== currentCounts.local
    );
  }

  /**
   * Get the key message index ID (for testing/debugging)
   */
  getKeyMessageIndexId(): string | null {
    return this.keyMessageIndexId;
  }

  /**
   * Reset the key message index ID (for testing/cleanup)
   */
  resetKeyMessageIndexId(): void {
    this.keyMessageIndexId = null;
  }
}
